import {
  CombinedSemester,
  Event,
  EventWaitingType,
  OrganizerType,
  PriorizationType
} from '@thro-types/events';

import { addHours, isBefore } from 'date-fns';
import { TFunction } from 'i18next';
import { pick } from 'lodash';
import * as yup from 'yup';

import { translations } from 'locales/i18n';
import { useAuthContext } from 'utils/hooks/useAuthContext';

export const eventValidationSchema = (t: TFunction<'translation'>, previousEvent?: Event) =>
  yup
    .object()
    .shape({
      name: yup.string().required(t(translations.validation.required())),
      description: yup.string().required(t(translations.validation.required())),
      isParticipantListVisible: yup.boolean().required(t(translations.validation.required())),
      hasSameGroupSize: yup.boolean().required(t(translations.validation.required())),
      isUnenrollAfterRegPeriodPossible: yup
        .boolean()
        .required(t(translations.validation.required())),
      visibleFrom: yup
        .date()
        .required(t(translations.validation.required()))
        .typeError(t(translations.validation.required())),
      registrationPeriodStart: yup
        .date()
        .required(t(translations.validation.required()))
        .typeError(t(translations.validation.required())),
      registrationPeriodEnd: yup
        .date()
        .required(t(translations.validation.required()))
        .typeError(t(translations.validation.required())),
      startDate: yup
        .date()
        .required(t(translations.validation.required()))
        .typeError(t(translations.validation.required())),
      endDate: yup
        .date()
        .required(t(translations.validation.required()))
        .typeError(t(translations.validation.required())),
      waitingType: yup
        .string()
        .oneOf(Object.values(EventWaitingType))
        .required(t(translations.validation.required())),
      priorizationType: yup
        .string()
        .oneOf(Object.values(PriorizationType))
        .required(t(translations.validation.required())),
      hostId: yup.number().required(t(translations.validation.required())),
      host: yup.string().required(t(translations.validation.required())),
      attachments: yup.mixed(),
      groups: yup.array().of(
        yup.object().shape({
          startDate: yup.date().nullable(),
          endDate: yup.date().nullable(),
          minParticipants: yup.number().required(t(translations.validation.required())),
          maxParticipants: yup.number().required(t(translations.validation.required())),
          place: yup.string().nullable()
        })
      ),
      organizers: yup.array().of(
        yup.object().shape({
          refId: yup.number().required(t(translations.validation.required())),
          organizerType: yup
            .string()
            .oneOf(Object.values(OrganizerType))
            .required(t(translations.validation.required()))
        })
      ),
      participantCriteria: yup.object().shape({
        entitlements: yup.array().of(yup.string()),
        combinedSemesters: yup.array().of(yup.string().oneOf(Object.values(CombinedSemester)))
      })
    })
    .test('maxParticipantValidCheck', function (value, context) {
      let isOk = true;
      if (!previousEvent) {
        return isOk;
      }
      if (isBefore(new Date(), new Date(previousEvent?.registrationPeriodStart))) {
        return isOk;
      }

      isOk = !context.options.context?.groups.find((group, index) => {
        if (!previousEvent.groups || !previousEvent.groups[index]) {
          return false;
        }
        const previousIndex = previousEvent.groups.findIndex(
          previousGroup => group.id === previousGroup.id
        );
        return group.maxParticipants < previousEvent?.groups[previousIndex].maxParticipants;
      });

      if (!isOk) {
        return this.createError({
          path: 'maxParticipant',
          message: t(translations.pages.events.tooltips.maxParticipantsTooltip())
        });
      }
      return isOk;
    });

export const useEventInitialValues = (initialEvent: Event | undefined) => {
  const { user: currentUser } = useAuthContext();

  return initialEvent
    ? {
        ...pick(initialEvent, [
          'id',
          'name',
          'description',
          'isParticipantListVisible',
          'hasSameGroupSize',
          'isUnenrollAfterRegPeriodPossible',
          'visibleFrom',
          'registrationPeriodEnd',
          'registrationPeriodStart',
          'waitingType',
          'startDate',
          'endDate',
          'calendarInvite',
          'attachments',
          'priorizationType',
          'hostId',
          'groups',
          'participantCriteria',
          'organizers',
          'occupancy',
          'host',
          'distributionStatus'
        ])
      }
    : {
        name: '',
        description: '',
        isParticipantListVisible: false,
        hasSameGroupSize: false,
        isUnenrollAfterRegPeriodPossible: false,
        visibleFrom: null,
        registrationPeriodStart: new Date(),
        registrationPeriodEnd: addHours(new Date(), 24),
        startDate: null,
        endDate: null,
        calendarInvite: true,
        waitingType: '',
        priorizationType: '',
        hostId: undefined,
        host: `${currentUser?.firstName} ${currentUser?.lastName}`,
        groups: [
          {
            startDate: null,
            endDate: null,
            minParticipants: 0,
            maxParticipants: 1,
            place: ''
          }
        ],
        participantCriteria: { entitlements: [], combinedSemesters: [] },
        organizers: [],
        attachments: []
      };
};
